import React from 'react';
import { Card, Space, Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { queries } from '../../../graphql/graphql';
import PaginatedAdminList, { ListItem } from '../../../components/containers/PaginatedAdminList';

const { Text } = Typography;

interface OrderListItem extends ListItem {
  type?: string;
  status?: string;
  createdAt?: string;
  createdById?: string;
  createdBy?: {
    id?: string;
    name?: string;
    email?: string;
  };
}

interface CustomCardProps {
  item: ListItem;
  refetch: () => void;
  prefix: string;
  roles?: string[];
}

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const getTypeLabel = (type?: string) => {
  if (type === 'SITE_PLAN') return 'Site plan';
  if (type === 'PROPOSAL') return 'Proposal';
  return 'Order';
};

const getStatusLabel = (status?: string) => {
  if (status === 'DONE') return 'Done';
  if (status === 'WAITING') return 'Waiting';
  return 'Unknown';
};

const getStatusColor = (status?: string) => {
  if (status === 'DONE') return 'green';
  if (status === 'WAITING') return 'gold';
  return 'default';
};

const CustomCard: React.FC<CustomCardProps> = ({ item }) => {
  const order = item as OrderListItem;
  const createdByLabel =
    order.createdBy?.name || order.createdBy?.email || order.createdById || 'Unknown user';

  return (
    <Card
      hoverable
      actions={[
        <Link key={`view-${order.id}`} to={`/admin/order/${order.id}`}>
          View
        </Link>,
        <Link key={`edit-${order.id}`} to={`/admin/order/edit/${order.id}`}>
          Edit
        </Link>,
      ]}>
      <Card.Meta
        title={order.title || `Order ${order.id}`}
        description={
          <Space direction="vertical" size={6}>
            <Space size={8}>
              <Tag color="blue">{getTypeLabel(order.type)}</Tag>
              <Tag color={getStatusColor(order.status)}>{getStatusLabel(order.status)}</Tag>
            </Space>
            <Text type="secondary">Created by: {createdByLabel}</Text>
            <Text type="secondary">Created at: {formatDate(order.createdAt)}</Text>
          </Space>
        }
      />
    </Card>
  );
};

const CustomCardWrapper: React.FC<{
  item: ListItem;
  refetch: () => void;
  prefix: string;
  roles?: string[];
}> = ({ item, refetch, prefix, roles }) => (
  <CustomCard item={item} refetch={refetch} prefix={prefix} roles={roles} />
);

const ListOrders: React.FC = () => {
  return (
    <PaginatedAdminList
      prefix="order"
      query={queries.order.ORDERS}
      inline={false}
      editable={true}
      canCreate={false}
      adminView={true}
      CustomCard={CustomCardWrapper}
      defaultPageSize={12}
    />
  );
};

export default ListOrders;
